import { useEffect, useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  ArrowLeft,
  Plus,
  Trash2,
  EyeOff,
  Save,
  Rocket,
  ExternalLink,
  Copy,
  BarChart3,
  Trophy,
  Monitor,
  Tablet,
  Smartphone,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { getApiErrorMessage } from '@/lib/utils'
import {
  useLanding,
  useUpdateLanding,
  usePublishLanding,
  fetchLandingPreview,
  landingPublicUrl,
} from '@/hooks/useLandingPages'
import type { Landing, LandingBlock, LandingVariant } from '@/types'
import { makeVariant, nextVariantId, cloneBlocks } from './blockDefaults'
import { BlockListEditor } from './BlockListEditor'
import { PreviewFrame } from './PreviewFrame'
import { type PreviewDevice, useDebouncedValue } from './previewUtils'

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'default'> = {
  published: 'success',
  draft: 'warning',
  archived: 'default',
}
const STATUS_LABEL: Record<string, string> = {
  published: 'Publicada',
  draft: 'Borrador',
  archived: 'Archivada',
}

export const LandingPageEditorPage = ({ pageId }: { pageId: string }) => {
  const { data, isLoading } = useLanding(pageId)
  const updateLanding = useUpdateLanding()
  const publishLanding = usePublishLanding()

  const [draft, setDraft] = useState<Landing | null>(null)
  const [activeVariant, setActiveVariant] = useState<string | null>(null)
  const [openBlock, setOpenBlock] = useState<string | null>(null)
  const [device, setDevice] = useState<PreviewDevice>('desktop')
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewLoading, setPreviewLoading] = useState(false)

  useEffect(() => {
    if (data && !draft) setDraft(data)
  }, [data, draft])

  const previewPayload = useMemo(() => {
    if (!draft) return null
    const common = {
      title: draft.title,
      blocks: draft.blocks,
      seo: draft.seo,
      custom_css: draft.custom_css,
      custom_head: draft.custom_head,
    }
    return activeVariant
      ? { ...common, variants: draft.variants, variant_id: activeVariant }
      : { ...common, variants: [] }
  }, [draft, activeVariant])

  const debouncedPayload = useDebouncedValue(previewPayload, 500)

  useEffect(() => {
    if (!debouncedPayload) return
    let active = true
    setPreviewLoading(true)
    fetchLandingPreview(debouncedPayload)
      .then((html) => {
        if (active) setPreviewHtml(html)
      })
      .catch(() => {})
      .finally(() => {
        if (active) setPreviewLoading(false)
      })
    return () => {
      active = false
    }
  }, [debouncedPayload])

  if (isLoading || !draft) {
    return <div className="p-8 text-muted-foreground">Cargando…</div>
  }

  const patch = (changes: Partial<Landing>) => setDraft((d) => (d ? { ...d, ...changes } : d))

  // ---- A/B variants ----
  const variants = draft.variants ?? []
  const sumWeights = variants.reduce((s, v) => s + (Number(v.weight) || 0), 0) || 1
  const pct = (v: LandingVariant) => Math.round(((Number(v.weight) || 0) / sumWeights) * 100)

  const activeBlocks = activeVariant
    ? (variants.find((v) => v.id === activeVariant)?.blocks ?? [])
    : draft.blocks

  const setActiveBlocks = (blocks: LandingBlock[]) => {
    if (activeVariant == null) {
      patch({ blocks })
      return
    }
    patch({ variants: variants.map((v) => (v.id === activeVariant ? { ...v, blocks } : v)) })
  }

  const activateAB = () => {
    patch({ variants: [makeVariant('a', draft.blocks), makeVariant('b', draft.blocks)] })
    setActiveVariant('a')
    setOpenBlock(null)
  }
  const addVariant = () => {
    const id = nextVariantId(variants)
    const base = activeVariant
      ? (variants.find((v) => v.id === activeVariant)?.blocks ?? draft.blocks)
      : draft.blocks
    patch({ variants: [...variants, makeVariant(id, base)] })
    setActiveVariant(id)
    setOpenBlock(null)
  }
  const updateVariant = (id: string, changes: Partial<LandingVariant>) =>
    patch({ variants: variants.map((v) => (v.id === id ? { ...v, ...changes } : v)) })
  const removeVariant = (id: string) => {
    const next = variants.filter((v) => v.id !== id)
    patch({ variants: next })
    if (activeVariant === id) setActiveVariant(next.length ? next[0].id : null)
    setOpenBlock(null)
  }
  const disableAB = () => {
    patch({ variants: [] })
    setActiveVariant(null)
    setOpenBlock(null)
  }

  const save = async (): Promise<Landing | null> => {
    try {
      const updated = await updateLanding.mutateAsync({
        id: pageId,
        updates: {
          title: draft.title,
          blocks: draft.blocks,
          variants: draft.variants,
          seo: draft.seo,
          custom_css: draft.custom_css,
          custom_head: draft.custom_head,
        },
      })
      setDraft(updated)
      toast.success('Cambios guardados')
      return updated
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al guardar'))
      return null
    }
  }

  const publish = async () => {
    const saved = await save()
    if (!saved) return
    try {
      const updated = await publishLanding.mutateAsync(pageId)
      setDraft(updated)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al publicar'))
    }
  }

  const unpublish = async () => {
    try {
      const updated = await updateLanding.mutateAsync({ id: pageId, updates: { status: 'draft' } })
      setDraft(updated)
      toast.success('Landing despublicada')
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error'))
    }
  }

  const promoteWinner = async (id: string) => {
    const win = variants.find((v) => v.id === id)
    if (!win) return
    try {
      const updated = await updateLanding.mutateAsync({
        id: pageId,
        updates: {
          title: draft.title,
          blocks: cloneBlocks(win.blocks),
          variants: [],
          seo: draft.seo,
          custom_css: draft.custom_css,
          custom_head: draft.custom_head,
        },
      })
      setDraft(updated)
      setActiveVariant(null)
      setOpenBlock(null)
      toast.success(`${win.name} promovida a contenido principal`)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al promover'))
    }
  }

  const publicUrl = landingPublicUrl(draft.slug, draft.public_url)
  const copyUrl = () => {
    navigator.clipboard?.writeText(publicUrl)
    toast.success('URL copiada')
  }

  const editingVariant = activeVariant ? variants.find((v) => v.id === activeVariant) : null

  return (
    <div className="flex h-[calc(100vh-2rem)] flex-col gap-3">
      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/landing-pages">
              <ArrowLeft className="mr-1 h-4 w-4" />
              Landings
            </Link>
          </Button>
          <Badge variant={STATUS_VARIANT[draft.status] ?? 'default'}>
            {STATUS_LABEL[draft.status] ?? draft.status}
          </Badge>
          <span className="font-mono text-xs text-muted-foreground">/l/{draft.slug}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={copyUrl}>
            <Copy className="mr-1 h-4 w-4" />
            Copiar URL
          </Button>
          {draft.status === 'published' && (
            <Button variant="outline" size="sm" asChild>
              <a href={publicUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="mr-1 h-4 w-4" />
                Abrir
              </a>
            </Button>
          )}
          {variants.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Trophy className="mr-1 h-4 w-4" />
                  Promover ganadora
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                {variants.map((v) => (
                  <DropdownMenuItem key={v.id} onClick={() => promoteWinner(v.id)}>
                    {v.name} · {pct(v)}%
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button variant="outline" size="sm" asChild>
            <Link to="/landing-pages/$pageId/analytics" params={{ pageId }}>
              <BarChart3 className="mr-1 h-4 w-4" />
              Analítica
            </Link>
          </Button>
          <Button variant="outline" size="sm" onClick={save} disabled={updateLanding.isPending}>
            <Save className="mr-1 h-4 w-4" />
            Guardar
          </Button>
          {draft.status === 'published' ? (
            <Button variant="secondary" size="sm" onClick={unpublish}>
              <EyeOff className="mr-1 h-4 w-4" />
              Despublicar
            </Button>
          ) : (
            <Button size="sm" onClick={publish} disabled={publishLanding.isPending}>
              <Rocket className="mr-1 h-4 w-4" />
              Publicar
            </Button>
          )}
        </div>
      </div>

      {/* Body: editor + preview */}
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[420px_1fr]">
        {/* Left: controls */}
        <div className="flex min-h-0 flex-col overflow-y-auto rounded-lg border bg-card p-4">
          <Tabs defaultValue="content">
            <TabsList className="mb-4 w-full">
              <TabsTrigger value="content" className="flex-1">
                Contenido
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex-1">
                Ajustes
              </TabsTrigger>
            </TabsList>

            <TabsContent value="content" className="space-y-3">
              <div className="grid gap-1.5">
                <Label className="text-xs">Título interno</Label>
                <Input value={draft.title} onChange={(e) => patch({ title: e.target.value })} />
              </div>

              {/* A/B variant bar */}
              {variants.length === 0 ? (
                <div className="rounded-md border border-dashed p-3 text-sm">
                  <p className="mb-2 text-muted-foreground">
                    Prueba dos versiones de esta landing y mide cuál convierte mejor.
                  </p>
                  <Button variant="outline" size="sm" onClick={activateAB}>
                    Activar prueba A/B
                  </Button>
                </div>
              ) : (
                <div className="space-y-3 rounded-md border p-3">
                  <div className="flex flex-wrap items-center gap-1">
                    <span className="mr-1 text-xs font-semibold text-muted-foreground">A/B</span>
                    <Button
                      size="sm"
                      variant={activeVariant == null ? 'primary' : 'outline'}
                      onClick={() => {
                        setActiveVariant(null)
                        setOpenBlock(null)
                      }}
                    >
                      Plantilla
                    </Button>
                    {variants.map((v) => (
                      <Button
                        key={v.id}
                        size="sm"
                        variant={activeVariant === v.id ? 'primary' : 'outline'}
                        onClick={() => {
                          setActiveVariant(v.id)
                          setOpenBlock(null)
                        }}
                      >
                        {v.name} · {pct(v)}%
                      </Button>
                    ))}
                    <Button size="sm" variant="ghost" onClick={addVariant} title="Añadir variante">
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {editingVariant && (
                    <div className="grid items-end gap-2 sm:grid-cols-[1fr_auto_auto]">
                      <div className="grid gap-1">
                        <Label className="text-xs">Nombre</Label>
                        <Input
                          value={editingVariant.name ?? ''}
                          onChange={(e) =>
                            updateVariant(editingVariant.id, { name: e.target.value })
                          }
                        />
                      </div>
                      <div className="grid gap-1">
                        <Label className="text-xs">Peso</Label>
                        <Input
                          type="number"
                          min={1}
                          className="w-20"
                          value={editingVariant.weight ?? 1}
                          onChange={(e) =>
                            updateVariant(editingVariant.id, {
                              weight: Math.max(1, Number(e.target.value) || 1),
                            })
                          }
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeVariant(editingVariant.id)}
                        title="Eliminar variante"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">
                    Con A/B activo el tráfico se reparte entre variantes (sticky por sesión). La
                    “Plantilla” no se sirve; úsala como base para nuevas variantes.
                  </p>
                  <Button variant="ghost" size="sm" onClick={disableAB}>
                    Desactivar A/B
                  </Button>
                </div>
              )}

              <BlockListEditor
                blocks={activeBlocks}
                onChange={setActiveBlocks}
                openBlock={openBlock}
                setOpenBlock={setOpenBlock}
              />
            </TabsContent>

            <TabsContent value="settings" className="space-y-3">
              <div className="grid gap-1.5">
                <Label className="text-xs">Slug (URL)</Label>
                <Input
                  value={draft.slug}
                  onChange={(e) => patch({ slug: e.target.value })}
                  className="font-mono"
                />
                <p className="text-xs text-muted-foreground">
                  Se aplica al guardar; si ya existe se ajusta automáticamente.
                </p>
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs">SEO · Título</Label>
                <Input
                  value={draft.seo?.title ?? ''}
                  onChange={(e) => patch({ seo: { ...draft.seo, title: e.target.value } })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs">SEO · Descripción</Label>
                <Textarea
                  value={draft.seo?.description ?? ''}
                  onChange={(e) => patch({ seo: { ...draft.seo, description: e.target.value } })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs">SEO · Imagen (Open Graph)</Label>
                <Input
                  value={draft.seo?.image ?? ''}
                  onChange={(e) => patch({ seo: { ...draft.seo, image: e.target.value } })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs">CSS global</Label>
                <Textarea
                  value={draft.custom_css ?? ''}
                  rows={6}
                  className="font-mono text-xs"
                  onChange={(e) => patch({ custom_css: e.target.value })}
                />
              </div>
              <div className="grid gap-1.5">
                <Label className="text-xs">HTML en &lt;head&gt; (fuentes, pixels…)</Label>
                <Textarea
                  value={draft.custom_head ?? ''}
                  rows={4}
                  className="font-mono text-xs"
                  onChange={(e) => patch({ custom_head: e.target.value })}
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right: preview */}
        <div className="flex min-h-0 flex-col gap-2">
          <div className="flex items-center justify-center gap-1">
            <Button
              variant={device === 'desktop' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setDevice('desktop')}
            >
              <Monitor className="mr-1 h-4 w-4" />
              1440
            </Button>
            <Button
              variant={device === 'tablet' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setDevice('tablet')}
            >
              <Tablet className="mr-1 h-4 w-4" />
              768
            </Button>
            <Button
              variant={device === 'mobile' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setDevice('mobile')}
            >
              <Smartphone className="mr-1 h-4 w-4" />
              390
            </Button>
          </div>
          <div className="min-h-0 flex-1">
            <PreviewFrame html={previewHtml} device={device} loading={previewLoading} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default LandingPageEditorPage
